// @ts-nocheck
import { Grid, IconButton } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { BLUE } from '../_config/consts';
import EnergyAlertsList from '../components/EnergyManagement/Overview/EnergyAlertsList.jsx';
import EnergyAvoidedSection from '../components/EnergyManagement/Overview/EnergyAvoidedSection.jsx';
import EnergyKpiCard from '../components/EnergyManagement/Overview/EnergyKpiCard.jsx';
import EnergyOverviewCard, {
  EnergyEmptyCardState,
  EnergyLoadingCardState,
  ENERGY_TEXT_PRIMARY,
  ENERGY_TEXT_SECONDARY,
} from '../components/EnergyManagement/Overview/EnergyOverviewCard.jsx';
import PowerOnHoursChart from '../components/EnergyManagement/Overview/PowerOnHoursChart.jsx';
import { computeOverviewFromUnits } from '../components/EnergyManagement/energyOverviewUtils';
import MDButton from '../components/MDButton/MDButton.jsx';
import PresentationalViewHeader from '../components/PresentationalView/PresentationalViewHeader.jsx';
import * as ModalActions from '../redux/actions/modal.actions';
import * as PowerControlUnitsActions from '../redux/actions/powerControlUnits.actions';
import * as UtilsActions from '../redux/actions/utils.actions';

const SECTION_STATUS = {
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  ERROR: 'ERROR',
};

const POWER_ON_HOURS_DAYS = 30;
const CHART_CARD_HEIGHT = 220;

@connect(state => ({
  powerControlUnits: state.powerControlUnits,
  themeName: state.settings.items.theme.data.themeName,
}))
class EnergyManagementOverview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      units: [],
      sectionsStatus: {
        units: SECTION_STATUS.LOADING,
        overview: SECTION_STATUS.LOADING,
        powerOnHours: SECTION_STATUS.LOADING,
        energySummary: SECTION_STATUS.LOADING,
        alerts: SECTION_STATUS.LOADING,
      },
    };
  }

  componentDidMount() {
    this.onFetchDashboard();
  }

  getPowerOnHoursInterval() {
    return {
      fromDate: moment().subtract(POWER_ON_HOURS_DAYS - 1, 'days').startOf('day').valueOf(),
      toDate: moment().endOf('day').valueOf(),
    };
  }

  setSectionStatus(section, status) {
    this.setState(prevState => ({ sectionsStatus: { ...prevState.sectionsStatus, [section]: status } }));
  }

  async runSection(section, request) {
    this.setSectionStatus(section, SECTION_STATUS.LOADING);
    try {
      const result = await request();
      this.setSectionStatus(section, SECTION_STATUS.LOADED);
      return result;
    } catch (error) {
      this.setSectionStatus(section, SECTION_STATUS.ERROR);
      return null;
    }
  }

  onFetchDashboard() {
    const { dispatch } = this.props;
    const { fromDate, toDate } = this.getPowerOnHoursInterval();
    // Every section loads on its own: an endpoint not yet available must not block the others
    this.runSection('units', async () => {
      const units = await dispatch(PowerControlUnitsActions.fetchAllPowerControlUnits());
      this.setState({ units });
    });
    this.runSection('overview', () => dispatch(PowerControlUnitsActions.fetchOverview()));
    this.runSection('powerOnHours', () => dispatch(PowerControlUnitsActions.fetchPowerOnHours(fromDate, toDate)));
    this.runSection('energySummary', () => dispatch(PowerControlUnitsActions.fetchEnergySummary(moment().year())));
    this.runSection('alerts', () => dispatch(PowerControlUnitsActions.fetchAlerts()));
  }

  async onExportReport() {
    const { dispatch } = this.props;
    const { fromDate, toDate } = this.getPowerOnHoursInterval();
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      await dispatch(PowerControlUnitsActions.exportReport(fromDate, toDate, 'pdf'));
    } catch (error) {
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="energyOverviewExportError" /></h6>),
        },
      }));
    } finally {
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onAlertClick(alert) {
    const { dispatch } = this.props;
    if (alert && alert.pcuId) {
      dispatch(push(`/energyManagement/units?pcuId=${alert.pcuId}`));
    }
  }

  getOverview() {
    const { powerControlUnits } = this.props;
    const { units, sectionsStatus } = this.state;
    if (sectionsStatus.overview === SECTION_STATUS.LOADED) {
      return powerControlUnits.overview;
    }
    return computeOverviewFromUnits(units);
  }

  renderKpis() {
    const { sectionsStatus } = this.state;
    const isLoading = sectionsStatus.overview !== SECTION_STATUS.LOADED && sectionsStatus.units === SECTION_STATUS.LOADING;
    const { totalUnits = 0, unitsOn = 0, unitsOff = 0, estimatedLoadKw = 0 } = this.getOverview();
    const formatValue = value => (isLoading ? '-' : value);
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <EnergyKpiCard label={<Entity entity="energyOverviewKpiUnits" />} value={formatValue(totalUnits)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnergyKpiCard
            label={<Entity entity="energyOverviewKpiOn" />}
            value={formatValue(unitsOn)}
            suffix={isLoading ? null : <Entity entity="energyOverviewOfTotal" data={{ total: totalUnits }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnergyKpiCard label={<Entity entity="energyOverviewKpiOff" />} value={formatValue(unitsOff)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnergyKpiCard
            label={<Entity entity="energyOverviewKpiLoad" />}
            value={formatValue(Number(estimatedLoadKw || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }))}
            suffix="kW"
          />
        </Grid>
      </Grid>
    );
  }

  renderPowerOnHours() {
    const { powerControlUnits: { powerOnHours } } = this.props;
    const { sectionsStatus } = this.state;
    let content = <EnergyEmptyCardState height={CHART_CARD_HEIGHT} />;
    if (sectionsStatus.powerOnHours === SECTION_STATUS.LOADING) {
      content = <EnergyLoadingCardState height={CHART_CARD_HEIGHT} />;
    } else if (sectionsStatus.powerOnHours === SECTION_STATUS.LOADED && !_.isEmpty(powerOnHours.days)) {
      content = <PowerOnHoursChart days={powerOnHours.days} height={CHART_CARD_HEIGHT} />;
    }
    return (
      <EnergyOverviewCard title={<Entity entity="energyOverviewPowerOnHoursTitle" data={{ days: POWER_ON_HOURS_DAYS }} />}>
        {content}
      </EnergyOverviewCard>
    );
  }

  renderAlerts() {
    const { powerControlUnits: { alerts } } = this.props;
    const { sectionsStatus } = this.state;
    // Alerts come only from the BE, already sorted by duration
    let content = <EnergyEmptyCardState height={CHART_CARD_HEIGHT} />;
    if (sectionsStatus.alerts === SECTION_STATUS.LOADING) {
      content = <EnergyLoadingCardState height={CHART_CARD_HEIGHT} />;
    } else if (sectionsStatus.alerts === SECTION_STATUS.LOADED) {
      content = <EnergyAlertsList alerts={alerts.content} maxHeight={CHART_CARD_HEIGHT} onAlertClick={alert => this.onAlertClick(alert)} />;
    }
    return (
      <EnergyOverviewCard title={<Entity entity="energyOverviewAlertsTitle" />}>
        {content}
      </EnergyOverviewCard>
    );
  }

  renderEnergyAvoided() {
    const { powerControlUnits: { energySummary } } = this.props;
    const { sectionsStatus } = this.state;
    let content = <EnergyEmptyCardState height={140} />;
    if (sectionsStatus.energySummary === SECTION_STATUS.LOADING) {
      content = <EnergyLoadingCardState height={140} />;
    } else if (sectionsStatus.energySummary === SECTION_STATUS.LOADED) {
      content = <EnergyAvoidedSection energySummary={energySummary} />;
    }
    return (
      <EnergyOverviewCard title={<Entity entity="energyOverviewEnergyAvoidedTitle" />}>
        {content}
      </EnergyOverviewCard>
    );
  }

  render() {
    const { sectionsStatus } = this.state;
    const isRefreshing = _.some(_.values(sectionsStatus), status => status === SECTION_STATUS.LOADING);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: 20, paddingBottom: 100 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontWeight: 'bold', color: ENERGY_TEXT_PRIMARY }}>
                  <Entity entity="energyOverviewTitle" />
                </h2>
                <IconButton
                  disabled={isRefreshing}
                  style={{ marginLeft: 6, opacity: isRefreshing ? 0.5 : 1 }}
                  onClick={() => this.onFetchDashboard()}
                >
                  <RefreshIcon style={{ fontSize: 24, color: BLUE }} />
                </IconButton>
              </div>
              <h5 style={{ margin: 0, marginTop: 4, color: ENERGY_TEXT_SECONDARY }}>
                <Entity entity="energyOverviewSubtitle" />
              </h5>
            </div>
            <MDButton
              fullWidth={false}
              backgroundColor={BLUE}
              containerStyle={{ margin: 0 }}
              title={<Entity entity="energyOverviewExportReport" />}
              onClick={() => this.onExportReport()}
            />
          </div>
          {this.renderKpis()}
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={8}>
              {this.renderPowerOnHours()}
            </Grid>
            <Grid item xs={12} md={4}>
              {this.renderAlerts()}
            </Grid>
          </Grid>
          <div style={{ marginTop: 16 }}>
            {this.renderEnergyAvoided()}
          </div>
        </div>
      </div>
    );
  }
}

export default EnergyManagementOverview;
